import type { RefObject } from 'react'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons'
import { faSeedling, faSparkles } from '@fortawesome/pro-solid-svg-icons'

import { Button, Icon, Image, Text, Textarea, Toggle } from '@/ui'
import { TypographyStyles, TextColors, mtc } from '@/styles'
import type { SeparationQuestion } from '@/features/onboarding/separation'
import { transactionIdsOf } from '@/features/onboarding/separation/transactionIds'
import { TransactionRow } from './TransactionRow'
import { hasUnsureRows, unsureTransactionCount } from './booksQuestionAnswers'
import { ONBOARDING_SCREEN } from '../shared/onboardingScreen.styles'
import { plural } from '../shared/plural'
import { BOOKS_QUESTION } from './booksQuestion.styles'

const MerchantIcon = ({ question }: { question: SeparationQuestion }) => {
  if ('src' in question.headerIcon) {
    return (
      <Image
        src={question.headerIcon.src}
        alt={question.headerName}
        className={mtc(
          BOOKS_QUESTION.merchantIcon,
          'border border-neutral-100'
        )}
      />
    )
  }

  return (
    <div className={BOOKS_QUESTION.outlinedIconBox}>
      <Icon
        icon={question.headerIcon.icon}
        color="text-neutral-600"
        size="lg"
      />
    </div>
  )
}

const validationMessage = ({
  saveFailed,
  showSelectionError,
}: {
  saveFailed: boolean
  showSelectionError: boolean
}): string => {
  if (saveFailed) {
    return "We couldn't save that. Check your connection and press Continue again."
  }
  if (showSelectionError) return 'Please select an option before continuing.'
  return 'Mark each transaction below before continuing.'
}

const UnsureButton = ({
  value,
  selected,
  onSelect,
}: {
  value: string
  selected: boolean
  onSelect: (option: string) => void
}) => (
  <Button
    variant="ghost"
    size="sm"
    aria-pressed={selected}
    onClick={() => onSelect(value)}
    className="mr-auto aria-pressed:bg-neutral-100"
  >
    I&apos;m not sure
  </Button>
)

/** Which Haven entry point was used, so analytics can tell the header button from the note link. */
export type HavenEntryScope = 'group' | 'unsure_note'

interface QuestionCardProps {
  question: SeparationQuestion
  selectedOption: string | null
  subItemSelections: Record<string, string>
  note: string
  expandedRows: Record<string, boolean>
  isExpanded: boolean
  isExpandedView: boolean
  showSelectionError: boolean
  showSubItemError: boolean
  showNoteError: boolean
  saveFailed: boolean
  scrollportRef: RefObject<HTMLDivElement>
  scrollportPaddingTop: number
  onExpandCard: () => void
  onSelectOption: (option: string) => void
  onSelectSubItem: (subItemId: string, value: string) => void
  onNoteChange: (note: string) => void
  onToggleRow: (subItemId: string) => void
  onAskHavenAboutGroup: (scope: HavenEntryScope) => void
}

const UnsureNote = ({
  note,
  unsureCount,
  showError,
  onChange,
  onAskHaven,
}: {
  note: string
  unsureCount: number
  showError: boolean
  onChange: (note: string) => void
  onAskHaven: () => void
}) => (
  <div className={BOOKS_QUESTION.unsureNote}>
    <Text variant={TypographyStyles.LABEL_SM} color={TextColors.HEADER}>
      Add a note for your bookkeeper
    </Text>
    <Textarea
      aria-label="Note for your bookkeeper"
      value={note}
      onChange={(event) => onChange(event.target.value)}
      placeholder={`${plural(unsureCount, 'What was this for?', 'What were these for?')} A quick note helps your bookkeeper get it right.`}
      rows={3}
    />
    {showError && !note.trim() && (
      <Text variant={TypographyStyles.BODY_XS} color={TextColors.DANGER}>
        Add a note before continuing.
      </Text>
    )}
    <Button
      variant="link"
      size="sm"
      iconLeft={faSeedling}
      onClick={onAskHaven}
      className="h-auto max-w-full shrink self-start px-0 text-left whitespace-normal"
    >
      Not sure? Get an answer from Haven now →
    </Button>
  </div>
)

const GroupAnswerHint = ({
  question,
  selectedOption,
  isExpandedView,
}: Pick<
  QuestionCardProps,
  'question' | 'selectedOption' | 'isExpandedView'
>) => {
  const count = transactionIdsOf(question).length
  if (count < 2 || selectedOption === question.expandValue) return null
  return (
    <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
      Your choice answers all {count.toLocaleString()} transactions.{' '}
      {isExpandedView
        ? 'Change any below that are different.'
        : 'Open the list to change any that are different.'}
    </Text>
  )
}

const showsUnsureNote = ({
  question,
  selectedOption,
  subItemSelections,
  isExpandedView,
}: Pick<
  QuestionCardProps,
  'question' | 'selectedOption' | 'subItemSelections' | 'isExpandedView'
>): boolean =>
  (isExpandedView || selectedOption === question.unsureValue) &&
  hasUnsureRows(question, subItemSelections)

export const QuestionCard = ({
  question,
  selectedOption,
  subItemSelections,
  note,
  expandedRows,
  isExpanded,
  isExpandedView,
  showSelectionError,
  showSubItemError,
  showNoteError,
  saveFailed,
  scrollportRef,
  scrollportPaddingTop,
  onExpandCard,
  onSelectOption,
  onSelectSubItem,
  onNoteChange,
  onToggleRow,
  onAskHavenAboutGroup,
}: QuestionCardProps) => (
  <div className={mtc(ONBOARDING_SCREEN.surfaceCard, 'gap-3')}>
    <div
      ref={scrollportRef}
      className={mtc(
        BOOKS_QUESTION.stickyTop,
        (isExpandedView ||
          showsUnsureNote({
            question,
            selectedOption,
            subItemSelections,
            isExpandedView,
          })) &&
          BOOKS_QUESTION.stickyTopOverRows
      )}
      style={{ top: -scrollportPaddingTop }}
    >
      <button
        type="button"
        className={BOOKS_QUESTION.cardHeader}
        onClick={onExpandCard}
        aria-expanded={isExpandedView}
        disabled={question.alwaysExpanded}
      >
        <MerchantIcon question={question} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Text
            variant={TypographyStyles.LABEL_MD}
            color={TextColors.HEADER}
            className={BOOKS_QUESTION.merchantName}
          >
            {question.headerName}
          </Text>
          {/* The mobile copy of the amount. Both copies are always in the DOM and swapped
              by breakpoint, so this one is hidden from assistive tech to stop the total
              being announced twice. */}
          <Text
            aria-hidden
            variant={TypographyStyles.LABEL_MD}
            color={TextColors.HEADER}
            className="hidden max-sm:block"
          >
            {question.amount}
          </Text>
          <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
            {question.headerSubtext}
          </Text>
        </div>
        <div className="flex shrink-0 cursor-pointer items-center gap-4">
          <Text
            variant={TypographyStyles.LABEL_MD}
            color={TextColors.HEADER}
            className="max-sm:hidden"
          >
            {question.amount}
          </Text>
          <Icon
            icon={
              !question.alwaysExpanded && isExpanded
                ? faChevronUp
                : faChevronDown
            }
            size="xs"
            color="text-neutral-900"
          />
        </div>
      </button>

      <div className="flex w-full gap-2 max-sm:flex-col">
        {question.toggleOptions.map((option) => (
          <Toggle
            key={option.value}
            variant="card"
            size="card"
            checked={selectedOption === option.value}
            onCheckedChange={() => onSelectOption(option.value)}
            className="flex-1 max-sm:w-full max-sm:flex-none"
          >
            <Text
              variant={TypographyStyles.LABEL_SM}
              color={TextColors.EMPHASIS}
              className="inline-flex items-center gap-2"
            >
              <span>{option.label}</span>
              {question.suggested === option.value && (
                <span className="text-xs font-medium text-green-100">
                  Suggested
                </span>
              )}
            </Text>
            <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
              {option.caption}
            </Text>
          </Toggle>
        ))}
      </div>

      <GroupAnswerHint
        question={question}
        selectedOption={selectedOption}
        isExpandedView={isExpandedView}
      />

      <div className="flex w-full min-w-0 justify-end gap-2">
        {question.unsureValue && (
          <UnsureButton
            value={question.unsureValue}
            selected={selectedOption === question.unsureValue}
            onSelect={onSelectOption}
          />
        )}
        <Button
          variant="ghost"
          size="sm"
          iconLeft={faSparkles}
          onClick={() => onAskHavenAboutGroup('group')}
          className="min-w-0"
        >
          <span className="truncate">
            Ask Haven
            <span className="max-sm:hidden">
              {' '}
              about{' '}
              {plural(
                question.subItems.length,
                'this transaction',
                'these transactions'
              )}
            </span>
          </span>
        </Button>
      </div>

      {(showSelectionError || showSubItemError || saveFailed) && (
        <div role="alert" className="flex items-center gap-1.5">
          <Text variant={TypographyStyles.BODY_XS} color={TextColors.DANGER}>
            {validationMessage({ saveFailed, showSelectionError })}
          </Text>
        </div>
      )}
    </div>

    {isExpandedView && (
      <div className="flex w-full flex-col">
        {question.subItems.map((subItem) => (
          <TransactionRow
            key={subItem.id}
            question={question}
            subItem={subItem}
            selection={subItemSelections[subItem.id]}
            expanded={Boolean(expandedRows[subItem.id])}
            needsAnswer={showSubItemError && !subItemSelections[subItem.id]}
            onToggleExpanded={() => onToggleRow(subItem.id)}
            onSelect={(value) => onSelectSubItem(subItem.id, value)}
          />
        ))}
      </div>
    )}

    {showsUnsureNote({
      question,
      selectedOption,
      subItemSelections,
      isExpandedView,
    }) && (
      <UnsureNote
        note={note}
        unsureCount={unsureTransactionCount(question, subItemSelections)}
        showError={showNoteError}
        onChange={onNoteChange}
        onAskHaven={() => onAskHavenAboutGroup('unsure_note')}
      />
    )}
  </div>
)
